import React, { useEffect, useState } from 'react';
import { ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../../components/Rodal/Rodal';
import Loader from 'react-loaders';
import './SectionEditorModal.scss';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import SectionsTable from './SectionsTable/SectionsTable';
import CreateNewSectionModal from './ManageSectionModal/ManageSectionModal';
import { DocumentSection } from '../../../slices/marketplace/timeline/models/document-section.model';
import Helpers from '../../../utils/helper';
import { DocumentDetails } from '../../../slices/marketplace/timeline/models/document-details.model';

interface SectionEditorModalProps {
    documentsSections: DocumentSection[];
    documentDetailsList: DocumentDetails[];
    isVisible: boolean;
    onClose: () => void;
    setDocumentSectionList: (documentSections: DocumentSection[]) => void;
}

const SectionEditorModal = (props: SectionEditorModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isCreateSectionModalVisible, setIsCreateSectionModalVisible] =
        useState(false);
    const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
        null
    );
    const [
        isRemoveConfirmationPopupVisible,
        setIsRemoveConfirmationPopupVisible,
    ] = useState(false);

    const [mode, setMode] = useState<'create' | 'edit'>('create');

    const addSection = async (sectionName: string): Promise<boolean> => {
        const newSection: DocumentSection = {
            id: Helpers.generateGUID(),
            name: sectionName,
            isLetterSection: false,
            index: props.documentsSections.length + 1,
            isAvailableForBuyers: false,
        };

        props.setDocumentSectionList([...props.documentsSections, newSection]);
        return true;
    };

    const editSection = async (
        sectionId: string,
        sectionName: string
    ): Promise<boolean> => {
        props.setDocumentSectionList(
            props.documentsSections.map((section) =>
                section.id === sectionId
                    ? { ...section, name: sectionName }
                    : section
            )
        );
        return true;
    };

    const removeSection = async (sectionId: string): Promise<boolean> => {
        const sections = props.documentsSections;

        if (sections.length === 1) {
            return false;
        }

        const sectionToRemove = sections.find(
            (section) => section.id === sectionId
        );
        const index = sectionToRemove.index;

        let updatedSections = sections.filter(
            (section) => section.id !== sectionId
        );

        updatedSections = updatedSections.map((section) => {
            if (section.index > index)
                return { ...section, index: section.index - 1 };
            return section;
        });

        if (sectionToRemove?.isLetterSection) {
            updatedSections = updatedSections.map((section, index) =>
                index === 0 ? { ...section, isLetterSection: true } : section
            );
        }

        props.setDocumentSectionList(updatedSections);
        return true;
    };

    const handleSectionToUpdate = (sectionId: string) => {
        setMode('edit');
        setSelectedSectionId(sectionId);
        setIsCreateSectionModalVisible(true);
    };

    const handleSectionToRemove = (sectionId: string) => {
        setSelectedSectionId(sectionId);
        setIsRemoveConfirmationPopupVisible(true);
    };

    const handleSectionMove = (sectionId: string, direction: 'up' | 'down') => {
        let sections = [...props.documentsSections];
        const index = sections.findIndex((section) => section.id === sectionId);
        if (direction === 'up') {
            if (index === 0) {
                const currentSection = sections[index];
                sections.splice(index, 1);
                sections = sections.map((section) => ({
                    ...section,
                    index: section.index - 1,
                }));
                const updatedSection = {
                    ...currentSection,
                    index: sections.length + 1,
                };
                sections.push(updatedSection);
            } else {
                const newPrevIndex = sections[index].index;
                const newCurrentIndex = sections[index - 1].index;
                sections[index - 1] = {
                    ...sections[index - 1],
                    index: newPrevIndex,
                };
                sections[index] = {
                    ...sections[index],
                    index: newCurrentIndex,
                };
            }
        } else if (direction === 'down') {
            if (index === sections.length - 1) {
                const currentSection = sections[index];
                sections.splice(index, 1);
                sections = sections.map((section) => ({
                    ...section,
                    index: section.index + 1,
                }));
                const updatedSection = { ...currentSection, index: 1 };
                sections.unshift(updatedSection);
            } else {
                const newNextIndex = sections[index].index;
                const newCurrentIndex = sections[index + 1].index;
                sections[index + 1] = {
                    ...sections[index + 1],
                    index: newNextIndex,
                };
                sections[index] = {
                    ...sections[index],
                    index: newCurrentIndex,
                };
            }
        }
        sections = sections.sort((a, b) => a.index - b.index);
        props.setDocumentSectionList(sections);
    };

    const handleBuyersAvailability = (
        sectionId: string,
        isAvailable: boolean
    ) => {
        const updatedSections = props.documentsSections.map((section) => {
            if (section.id === sectionId) {
                return { ...section, isAvailableForBuyers: isAvailable };
            }
            return section;
        });
        props.setDocumentSectionList(updatedSections);
    };

    const renderLoader = () => {
        return (
            <div
                className="loader-container"
                style={{ height: '100%', width: '100%' }}>
                <div className="loader-container-inner">
                    <br />
                    <div className="text-center">
                        <Loader active={isLoading} type="ball-scale-multiple" />
                    </div>
                    <h6 className="mt-5">Loading sections...</h6>
                </div>
            </div>
        );
    };

    const renderContent = () => {
        return (
            <div>
                {1 > 0 ? (
                    <div>
                        <SectionsTable
                            documentsSections={props.documentsSections}
                            handleSectionToUpdate={handleSectionToUpdate}
                            handleSectionToRemove={handleSectionToRemove}
                            handleSectionMove={handleSectionMove}
                            documentDetailsList={props.documentDetailsList}
                            handleBuyersAvailability={handleBuyersAvailability}
                        />
                    </div>
                ) : (
                    <div className="w-100 d-flex flex-column align-items-center no-content">
                        <div className="mb-3">No sections to display</div>
                        <div className="">
                            <button
                                className="btn btn-primary"
                                onClick={() => {}}>
                                Create new Section
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="section-editor-modal">
            <Rodal
                visible={props.isVisible}
                animation={'slideTop'}
                onClose={props.onClose}
                showMask={true}
                width={600}
                closeOnEsc
                className="modal-sections"
                center>
                <ModalHeader className="popup-header">
                    <div className="d-flex align-items-center">
                        <span>Sections</span>
                        <div className="ml-3">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    setMode('create');
                                    setIsCreateSectionModalVisible(true);
                                }}>
                                Add new
                            </button>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody>
                    {isLoading ? (
                        renderLoader()
                    ) : (
                        <div className="bootstrap-table-container">
                            {renderContent()}
                        </div>
                    )}
                </ModalBody>
            </Rodal>

            {isCreateSectionModalVisible ? (
                <CreateNewSectionModal
                    isVisible={isCreateSectionModalVisible}
                    onClose={() => setIsCreateSectionModalVisible(false)}
                    onAddSection={addSection}
                    onUpdateSection={mode === 'create' ? null : editSection}
                    mode={mode}
                    initialSectionName={
                        mode === 'create'
                            ? null
                            : props.documentsSections.find(
                                  (s) => s.id === selectedSectionId
                              )?.name
                    }
                    rowId={mode === 'create' ? null : selectedSectionId}
                />
            ) : (
                []
            )}

            <div className="confirmation-modal-container">
                <ConfirmationModal
                    isVisible={isRemoveConfirmationPopupVisible}
                    setIsVisible={setIsRemoveConfirmationPopupVisible}
                    header="Confirmation"
                    confirmationText={`Are you sure you want to remove '${
                        props.documentsSections.find(
                            (s) => s.id === selectedSectionId
                        )?.name
                    }' section?`}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    onConfirm={removeSection}
                    params={selectedSectionId}
                />
            </div>
        </div>
    );
};

export default SectionEditorModal;
